/**
 * superadminUsersService -- axios wrappers for the SA Users admin API.
 *
 * Card: SA Users page + per-user detail -- https://trello.com/c/tRC6K0Zn
 * Backend: `api/src/endpoints/superadmin_users.py`
 *
 * Platform-wide user administration -- every account across every
 * tenant. Distinct from:
 *   - `accountService` (legacy /admin/accounts surface being retired)
 *   - the distributor Team & Permissions API (distributor self-service)
 *   - the personal `/me/*` family (self-service profile)
 *
 * Conventions (matches aclAdminService.js):
 *   - Raw `api` (default import), no apiCall helper.
 *   - Methods return the axios promise; callers await and read `.data`.
 *   - No try/catch -- errors propagate so callers can surface the
 *     server's message (notably 409 on a duplicate username).
 *   - Auth header is automatic via the axios interceptor.
 *
 * @typedef {import('axios').AxiosResponse} AxiosResponse
 */
import api from "./api";

/**
 * Cross-tenant user list.
 * @param {object} params - q, distributor_id, client_id, role_id, active,
 *                          page, per_page. Empty values are dropped.
 * @returns {Promise<AxiosResponse<{rows: object[], total: number}>>}
 */
export function listUsers(params = {}) {
    const clean = {};
    for (const [key, value] of Object.entries(params)) {
        if (value !== null && value !== undefined && value !== "") {
            clean[key] = value;
        }
    }
    return api.get("/superadmin/users", { params: clean });
}

/**
 * Distributors / clients / roles available as filter values.
 * @returns {Promise<AxiosResponse<object>>}
 */
export function getFilterOptions() {
    return api.get("/superadmin/users/filter-options");
}

/**
 * Full per-user detail (identity, capabilities, overrides, activity,
 * delete eligibility).
 * @param {number|string} id
 * @returns {Promise<AxiosResponse<object>>}
 */
export function getUser(id) {
    return api.get(`/superadmin/users/${id}`);
}

/**
 * Live "is this username taken?" check.
 *
 * UX ONLY -- the server's unique constraint is the real guard, and a
 * write can still come back 409 after this said "available".
 *
 * @param {string} username
 * @param {number|null} excludeAccountId - the account being edited, so
 *        its own current username reads as available to itself.
 * @returns {Promise<AxiosResponse<{available: boolean}>>}
 */
export function checkUsernameAvailable(username, excludeAccountId = null) {
    const params = { username };
    if (excludeAccountId !== null && excludeAccountId !== undefined) {
        params.exclude_account_id = excludeAccountId;
    }
    return api.get("/superadmin/users/username-available", { params });
}

/**
 * Edit identity fields (first/last name, email, username, active).
 * Password is NOT settable here by design.
 * @returns {Promise<AxiosResponse<object>>}
 */
export function updateUser(id, payload) {
    return api.patch(`/superadmin/users/${id}`, payload);
}

/**
 * Create a user. Omit `password` to send a set-password email instead.
 * @returns {Promise<AxiosResponse<object>>}
 */
export function createUser(payload) {
    return api.post("/superadmin/users", payload);
}

/** Deactivate (the normal destructive action). */
export function deactivateUser(id, reason = "") {
    return api.post(`/superadmin/users/${id}/deactivate`, { reason });
}

/** Re-activate a deactivated account. */
export function activateUser(id, reason = "") {
    return api.post(`/superadmin/users/${id}/activate`, { reason });
}

/** Email the user a set-password link. */
export function forcePasswordReset(id, reason = "") {
    return api.post(`/superadmin/users/${id}/force-password-reset`, { reason });
}

/**
 * Clear a user's second factor -- TOTP AND passkeys.
 *
 * The account-recovery path for someone who lost their authenticator or
 * their device. Both are removed together on purpose: clearing TOTP
 * while leaving passkeys registered hands back an account the user
 * still cannot get into.
 *
 * Returns the refreshed `mfa` block. The caller must render THAT rather
 * than assuming a successful call means "off" -- the server is the
 * authority on security state.
 */
export function resetUserMfa(id, reason = "") {
    return api.post(`/superadmin/users/${id}/mfa/reset`, { reason });
}

/**
 * Whether the account can be hard-deleted, and the blocking references
 * when it cannot. The UI must consult this BEFORE offering the control.
 * @returns {Promise<AxiosResponse<{deletable: boolean, references: object[]}>>}
 */
export function getDeleteEligibility(id) {
    return api.get(`/superadmin/users/${id}/delete-eligibility`);
}

/** Hard-delete a never-used account. Server re-checks eligibility. */
export function deleteUser(id) {
    return api.delete(`/superadmin/users/${id}`);
}
